import { Component, Element, Fragment, State, getAssetPath, h } from '@stencil/core';
import state from '../../store';
import { Business } from '../../types';
import { postOrder } from '../../utils/api';
import { FelixOrder, postPerson } from '../../utils/felixApi';
import { hideField } from '../../utils/form';
import { scrollToTop } from '../../utils/scroll';

@Component({
  tag: 'hemfixarna-checkout',
  shadow: false,
})
export class HemfixarnaCheckout {
  @State() date: string = '';
  @State() dateError: string | null = null;
  @State() generalError: string | null = null;
  @State() loading = false;
  @State() enteredPin: string = '';
  @Element() private el: HTMLElement;
  @State() selectedSubSource: string | null = null;
  @State() partnerOrderId: string | null = null;
  @State() sellerID: string | null = null;
  @State() RefferalID: string | null = null;

  handleChangeDate = (e: Event) => {
    this.dateError = null;
    this.date = (e.target as HTMLInputElement).value;
  };

  handleChangeTerms = () => {
    this.generalError = null;
  };

  sendOrder = async () => {
    if (this.loading) return;
    this.loading = true;

    const startFees = state.cart
      .filter(item => item.start_fee_obj)
      .map(item => ({
        ID: item.start_fee_obj?.ID,
        title: item.start_fee_obj?.title,
        price: item.start_fee_obj?.price,
        rut: item.rut ? 1 : 0,
        rot: item.rot ? 1 : 0,
        green: item.green ? 1 : 0,
      }));

    let order: FelixOrder = {
      firstName: state.creditSafeUser.firstName,
      lastName: state.creditSafeUser.lastName,
      street: state.user.street,
      zip: state.user.zip,
      town: state.user.town,
      ssn: state.user.ssn,
      email: state.user.email,
      phone: state.user.phone,
      url: `${state.business === Business.string ? 'string' : state.business}-webk`,
      date: this.date,
      products: [
        ...state.cart.map(item => {
          const parts = item.parts.map(part => ({
            id: String(part.id),
            name: part.name,
            quantity: part.amount,
            rut: 0,
            rot: 0,
            start_fee_id: item.start_fee_obj ? item.start_fee_obj.ID : null,
          }));
          return [
            {
              id: String(item.id),
              name: item.name,
              quantity: item.amount,
              rot: item.rot ? 1 : 0,
              rut: item.rut ? 1 : 0,
              start_fee_id: item.start_fee_obj ? item.start_fee_obj.ID : null,
            },
            ...parts,
          ];
        }),
      ].flat(),
      start_fees: startFees,
      creditSafe: state.creditSafeUser,
      customer: state.business,
      id_source: state.customer.source ? state.customer.source.fieldData.So01_Name : '',
      id_sub_source: this.selectedSubSource ? this.selectedSubSource : '',
      seller_id: this.sellerID ?? '',
      referral_id: this.RefferalID ?? '',
    };

    const felixOrder = await postPerson(order);
    if (!felixOrder || !felixOrder.response?.scriptResult?.includes('OK')) {
      order = { ...order, felixStatus: 'error' };
    } else {
      order = { ...order, felixStatus: 'success' };
    }

    try {
      const { data: orderConfirm, status } = await postOrder(order);
      if (orderConfirm && status && status === 200) {
        state.step = 6;
        const el = this.el.closest('.hemfixarna_content');
        scrollToTop(el);
      } else {
        this.generalError = 'Något gick fel, försök igen senare';
      }
    } catch (error) {
      this.generalError = 'Något gick fel, försök igen senare';
    }
    this.loading = false;
  };

  handleSubmit = (e: Event) => {
    e.preventDefault();
    this.generalError = null;
    const validDate = this.date.length > 0;
    if (!validDate) {
      this.dateError = 'Ange ett giltigt datum';
    }
    const checkBoxes = Array.from(this.el.querySelectorAll('input[type="checkbox"]'));
    const notValidTerms = checkBoxes.find(i => !(i as HTMLInputElement).checked);
    if (notValidTerms) {
      this.generalError = 'Du måste godkänna villkoren';
    }
    if (state.customer.source && state.selectSource) {
      if (!this.sellerID) {
        this.generalError = 'Ange säljare';
        return;
      }
      if (state.customer.source.sub_sources?.length && !this.selectedSubSource) {
        this.generalError = 'Välj underkälla';
        return;
      }
    }
    if (validDate && !notValidTerms) {
      this.sendOrder();
    }
  };

  componentWillLoad() {
    if (Boolean(state.user && state.user.street)) {
      state.checkoutStep = 2;
    }
  }

  render = () => {
    const dateLogo = getAssetPath(`./assets/date.svg`);
    const down = getAssetPath(`./assets/down.svg`);
    const loading = getAssetPath(`./assets/spinner.gif`);

    if (state.checkoutEdit) {
      return <hemfixarna-address />;
    } else if (state.checkoutStep === 1) {
      return <hemfixarna-getuser />;
    } else if (state.checkoutStep === 2) {
      return (
        <div class="mb-2">
          <div class="hemfixarna_addressinfo">
            <div>
              <p>{hideField(state.user.firstName)}</p>
              <p>{hideField(state.user.lastName)}</p>
              <p>{state.user.email}</p>
              <p>{state.user.phone}</p>
            </div>
            <div>
              <p>{hideField(state.user.street)}</p>
              <p>{hideField(state.user.zip)}</p>
              <p>{hideField(state.user.town)}</p>
            </div>
            <button onClick={() => (state.checkoutEdit = true)}>Behöver du ändra adressen?</button>
          </div>
          <form onSubmit={e => this.handleSubmit(e)}>
            <div>
              <img src={dateLogo} width={24} />
              <input
                class={`${this.date.length ? 'input_active' : ''}`}
                min={new Date().toISOString().split('T')[0]}
                onChange={e => this.handleChangeDate(e)}
                type="date"
                name="date"
                value={this.date}
              />
              <label htmlFor="date">Tidigaste datum för hembesök</label>
              <img src={down} width={24} />
            </div>
            {this.dateError && <span>{this.dateError}</span>}
            {state.customer.source && state.selectSource ? (
              <Fragment>
                <p>
                  Inloggad som:
                  <strong> {state.customer.source.fieldData.So01_Name}</strong>
                </p>
                <label>
                  Säljare (SäljarId)
                  <input type="text" value={this.sellerID} onChange={e => (this.sellerID = (e.target as HTMLInputElement).value)} />
                </label>
                <label>
                  PartnerOrderID (RefferalID)
                  <input type="text" value={this.RefferalID} onChange={e => (this.RefferalID = (e.target as HTMLInputElement).value)} />
                </label>
                {state.customer.source.sub_sources?.length ? (
                  <div>
                    <select onChange={e => (this.selectedSubSource = (e.target as HTMLSelectElement).value)}>
                      <option disabled selected value="null">
                        Välj underkälla
                      </option>
                      {state.customer.source.sub_sources.map(sub => (
                        <option value={sub['So_SubSource::Sus01_Name']}>{sub['So_SubSource::Sus01_Name']}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <p>Det finns inga underkällor att välja</p>
                )}
              </Fragment>
            ) : null}
            <label class="hemfixarna_checkbox">
              <input onChange={() => this.handleChangeTerms()} type="checkbox" />
              <span innerHTML={state.options.terms} />
            </label>
            {state.cart
              .filter(i => i.terms_checkout?.length)
              .map(item => (
                <Fragment>
                  <label class="hemfixarna_checkbox">
                    <input onChange={() => this.handleChangeTerms()} type="checkbox" />
                    <span>{item.terms_checkout}</span>
                  </label>
                </Fragment>
              ))}
            {this.generalError && <span>{this.generalError}</span>}
            <div class={this.loading ? 'loading' : ''}>
              <input type="submit" value={this.loading ? '' : 'Skicka bokning'} />
              <img width={20} height={20} src={loading} alt="spinner" />
            </div>
          </form>
        </div>
      );
    }
  };
}
